import { IDeferPromise, IFRPCResponse } from "~/interfaces";
import * as Sentry from "@sentry/browser";
import errorLog from "~/es6/error-log";
import { CONFIG } from "~/config";
import Request, { Options } from "~/lib/net/request";
import { getFrpcErrorType } from "~/lib/net/errors";

/**
 * Metoda, vracející promise splněný po zadaném počtu milisekund
 * @param  {number} ms počet milisekund
 */
export function sleep(ms: number): Promise<void> {
	return new Promise(resolve => {
		setTimeout(resolve, ms);
	});
}

export function deferPromise<T>(): IDeferPromise<T> {
	let resolve: (data: T) => void = null;
	let reject: (error: any) => void = null;
	const promise = new Promise((resolveArg, rejectArg) => {
		resolve = resolveArg;
		reject = rejectArg;
	}) as Promise<T>;

	return {
		promise,
		resolve,
		reject,
	};
}

interface IFrpc {
	/** URL, kam se dotaz pošle */
	url: string;
	/** FRPC metoda, která se volá */
	method: string;
	/** argumenty FRPC metody */
	args: Array<any>;
	/** nápověda pro FRPC, aby vědělo, jaký datový typ na jakou položku použít */
	hints?: { [ key: string ]: string };
	headers?: { [ key: string ]: string };
	withCredentials?: boolean;
}

/** Obalení FRPC volání z JAKu promisem */
export function frpc<T extends IFRPCResponse>({
	url = "",
	method = "",
	args = [],
	hints = {},
	headers = {},
	withCredentials = true,
}: IFrpc): {
	promise: Promise<T>;
	request: { abort: () => void; };
} {
	const reqHeaders: Options["headers"] = [];

	Object.keys(headers).forEach(headerName => {
		reqHeaders.push({
			name: headerName,
			value: headers[headerName],
		});
	});

	// ab testy
	if (CONFIG.abTest) {
		reqHeaders.push({
			name: "X-AB-Feature",
			value: CONFIG.abTest.flags,
		});
	}

	const request = new Request(method, args, {
		headers: reqHeaders,
		hints,
		url,
		withCredentials,
	});

	const promise = new Promise((resolve, reject) => {
		request.getPromise().then(data => {
			resolve(data);
		}, error => {
			// logujeme
			const errorType = getFrpcErrorType(error);

			errorLog.add(`FRPC error|url=${url.replace(/https?:\/\/[^/]+/u, "")}};method=${method};error=${errorType}`);

			// nechceme logovat vsechno
			if (errorType === "timeout" || errorType === "http") {
				// jelikoz obvykle nad tou vracenou chybou nikde nedelame throw, tak ji logneme rovnou tady
				Sentry.withScope(scope => {
					scope.setTag("X-Correlation-Id", request.correlationId);
					scope.setTag("URL", url);
					scope.setTag("FRPC-method", method);
					Sentry.captureException(error);
				});
			}

			reject(error);
		});
	});

	return {
		promise: promise as Promise<T>,
		request: request.getXHR(),
	};
}
